import { Children, FC, ReactNode } from 'react';
import { Transition } from 'react-transition-group';
import { css, keyframes, useTheme } from '@emotion/react';
import { emotionClone } from '../../utils/helpers';
import { TransitionStatus } from 'react-transition-group/Transition';

export interface AnimationProp {
  animationEntering: any;
  animationExiting: any;
  exited: any;
  entered: any;
}

interface AnimationStyles {
  animationEntering: any;
  animationExiting: any;
  exited: any;
  entered: any;
}

interface MapUIAnimationProps {
  children: ReactNode;
  timeout: number;
  animation: AnimationStyles;
}

const MapUIAnimation: FC<MapUIAnimationProps> = ({
  children,
  timeout,
  animation = {},
  ...rest
}) => (
  <Transition timeout={timeout} {...rest}>
    {(status: TransitionStatus) => {
      const getStyle = () => {
        const { animationEntering, animationExiting, exited, entered } =
          animation;
        switch (status) {
          case 'entering':
            return css({
              animation: `${animationEntering} ${timeout}ms ease-in`,
            });
          case 'exiting':
            return css(exited, {
              animation: `${animationExiting} ${timeout}ms ease-in`,
            });
          case 'exited':
            return exited;
          case 'entered':
            return entered;
          default:
            return exited;
        }
      };

      return typeof children === 'function'
        ? children(getStyle())
        : emotionClone(Children.only(children), {
            css: getStyle(),
          });
    }}
  </Transition>
);

export const makeAnimationComponent = (animationName: string) => {
  const Animation = (props) => {
    const theme = useTheme();

    const { animationEntering, animationExiting, exited, entered } =
      theme.animations[animationName];

    const requiredProps = [
      entered,
      exited,
      animationEntering,
      animationExiting,
    ];

    const hasError = requiredProps.some((prop) => !prop);
    if (hasError) {
      console.error(
        `Animation ${animationName} provided without the necessary theme data`
      );
    }

    return (
      <MapUIAnimation
        animation={{
          animationEntering: keyframes`${animationEntering}`,
          animationExiting: keyframes`${animationExiting}`,
          exited: css(exited),
          entered: css(entered),
        }}
        {...props}
      />
    );
  };

  Animation.displayName = `MapUIAnimation-${animationName}`;

  return Animation;
};

export const Fade = makeAnimationComponent('fade');
export const SlideFade = makeAnimationComponent('slideFade');
export const SlideLeft = makeAnimationComponent('slideLeft');
export const SlideRight = makeAnimationComponent('slideRight');
export const SlideUp = makeAnimationComponent('slideUp');
export const SlideDown = makeAnimationComponent('slideDown');
export const SlideDownUp = makeAnimationComponent('slideDownUp');
export const SlideInOut = makeAnimationComponent('slideInOut');
export const SlideOutIn = makeAnimationComponent('slideOutIn');
